import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { usePortalInvalidate } from "@/hooks/usePortalQueries";
import { RecordFormModal } from "@/components/RecordFormModal";
import { ArchiveControls } from "@/components/ArchiveControls";
import { StatusChip } from "@/components/StatusChip";
import { EmptyState } from "@/components/EmptyState";
import { DetailSkeleton } from "@/components/Skeletons";
import { Section } from "@/components/Section";
import { RecordPageLayout } from "@/components/RecordPageLayout";
import { Users, Mail, Phone, Building2, Pencil, ExternalLink } from "lucide-react";

// Design System 2.0 (2026-10, Phase 5) — restyle only: same fields, same API, same testids.
// Now built on the shared RecordPageLayout (header) + Section (open sections) primitives
// instead of this page's own local Stat/Chip/Section consts. Purge moved into the "⋯"
// overflow menu (was a top-level button) — same ArchiveControls component, variant="menu".
export default function ContactDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasScope } = useAuth();
  const invalidatePortal = usePortalInvalidate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);

  const load = () => api.get(`/v1/contacts/${id}`)
    .then((r) => setData(r.data))
    .catch((e) => setError(e?.response?.status === 403 ? "restricted" : "not_found"))
    .finally(() => setLoading(false));
  const onContactChanged = (newClientId) => {
    load();
    invalidatePortal.contacts();
    if (data?.client?.id) invalidatePortal.client(data.client.id);
    if (newClientId && newClientId !== data?.client?.id) invalidatePortal.client(newClientId);
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <DetailSkeleton testId="contact-detail-skeleton" />;
  if (error) return <div className="p-8 text-sm text-faint" data-testid="contact-detail-error">{error === "restricted" ? "Access Restricted" : "Contact not found"}</div>;

  const c = data.contact;
  const client = data.client;
  // Phase 5 fix (2026-10, Job C2) — same pattern as PartnerDetail/ClientDetail: hide the "⋯"
  // trigger when its only item (ArchiveControls) would be hidden for this user.
  const hasContactOverflow = hasScope("contacts.create") || user?.role === "founder";

  return (
    <RecordPageLayout
      testId="contact-detail-page"
      titleTestId="contact-detail-name"
      archivedTestId="contact-archived-badge"
      activityTestId="contact-activity-badge"
      icon={Users}
      title={c.name}
      statusChip={<StatusChip status={c.status} testId="contact-status-chip" />}
      tags={[c.role || c.role_title, c.decision_power]}
      activityIso={c.last_activity_at}
      archived={c.archived}
      secondaryActions={hasScope("contacts.create") && (
        <button type="button" onClick={() => setEditing(true)} data-testid="contact-edit-btn"
          className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-paper px-3 py-1.5 text-xs text-ink-soft hover:border-brand hover:text-gold-text">
          <Pencil className="h-3.5 w-3.5" /> Edit
        </button>
      )}
      overflow={
        hasContactOverflow && (
          <ArchiveControls
            entity="contacts" variant="menu"
            record={{ id: c.id, name: c.name, archived: c.archived }}
            canWrite={hasScope("contacts.create")}
            isFounder={user?.role === "founder"}
            onChanged={onContactChanged}
            onPurged={() => { invalidatePortal.contacts(); if (client?.id) invalidatePortal.client(client.id); navigate("/contacts"); }}
          />
        )
      }
    >
      {client && (
        <Link to={`/clients/${client.id}`} data-testid="contact-detail-client-link" className="-mt-4 inline-flex items-center gap-1.5 text-sm text-ink-soft hover:text-gold-text">
          <Building2 className="h-3.5 w-3.5" /> {client.name}
        </Link>
      )}

      <RecordFormModal
        type="contact"
        mode="edit"
        open={editing}
        onClose={() => setEditing(false)}
        recordId={c.id}
        initialValues={{ name: c.name, role: c.role || c.role_title, email: c.email, phone: c.phone, decision_power: c.decision_power, status: c.status }}
        initialClientId={client?.id}
        initialClientName={client?.name}
        onSaved={(record, savedClientId) => onContactChanged(savedClientId)}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Stat label="Email" value={c.email ? <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1.5 hover:text-gold-text"><Mail className="h-3.5 w-3.5" /> {c.email}</a> : "—"} />
        <Stat label="Phone" value={c.phone ? <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {c.phone}</span> : "—"} />
      </div>

      <Section title="Projects" testId="contact-projects-section">
        {(data.projects || []).length === 0 ? (
          <EmptyState title="No linked projects" hint="Projects appear here once this contact is set as Primary Contact or added to a Project in Notion." testId="contact-projects-empty" />
        ) : (
          <div className="divide-y divide-hairline rounded-md border border-hairline bg-paper">
            {data.projects.map((p) => (
              <Link key={p.id} to={`/projects/${p.id}`} data-testid={`contact-project-row-${p.id}`} className="flex items-center justify-between px-4 py-3 text-sm text-ink hover:text-gold-text">
                {p.name} <ExternalLink className="h-3.5 w-3.5 text-faint" />
              </Link>
            ))}
          </div>
        )}
      </Section>
    </RecordPageLayout>
  );
}

const Stat = ({ label, value }) => (
  <div className="rounded-lg border border-hairline bg-paper p-4">
    <div className="text-[11px] uppercase tracking-wider text-faint">{label}</div>
    <div className="text-sm mt-1 text-ink">{value}</div>
  </div>
);
